import * as styles from "./AbsenceDetail.css";

import { clsx } from "clsx";
import { useAtomValue, useSetAtom } from "jotai";

import { Button } from "@frontend/components/atoms/Button";
import {
  DatePicker,
  DatePickerContent,
  DatePickerTrigger,
  DatePickerValue,
} from "@frontend/components/atoms/DatePicker";
import { Icon } from "@frontend/components/atoms/Icon";
import { Input } from "@frontend/components/atoms/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@frontend/components/atoms/Select";
import {
  TimePicker,
  TimePickerContent,
  TimePickerTrigger,
  TimePickerValue,
} from "@frontend/components/atoms/TimePicker";
import { useIntlLocale } from "@frontend/hooks/useIntlLocale";
import { Trans, useLingui } from "@lingui/react/macro";

import {
  absenceTypesAtom,
  absencesAtom,
  deselectAbsenceAtom,
  loadingAtom,
  patchDraftAtom,
  sameCaseCandidatesAtom,
  selectedDraftAtom,
  selectedTypeAtom,
} from "../stores";
import { draftDayCount, formatRange } from "../utils/format";
import { AbsenceOverview } from "./AbsenceOverview";
import { AbsenceSameCaseLabel } from "./AbsenceSameCaseLabel";
import { ReplacementList } from "./ReplacementList";
import { ValidatedHoursNotice } from "./ValidatedHoursNotice";

/** The option standing for "this absence starts a case of its own". */
const NEW_CASE = "0";

export function AbsenceDetail() {
  const { t } = useLingui();

  const draft = useAtomValue(selectedDraftAtom);
  const absences = useAtomValue(absencesAtom);
  const loading = useAtomValue(loadingAtom);
  const types = useAtomValue(absenceTypesAtom);
  const selectedType = useAtomValue(selectedTypeAtom);
  const sameCaseCandidates = useAtomValue(sameCaseCandidatesAtom);
  const locale = useIntlLocale();

  const patch = useSetAtom(patchDraftAtom);
  const deselect = useSetAtom(deselectAbsenceAtom);

  if (!draft) {
    return !loading && 0 === absences.length ? (
      <p className={styles.state}>
        <Trans>Select an absence, or add one.</Trans>
      </p>
    ) : (
      <AbsenceOverview />
    );
  }

  const dayCount = draftDayCount(draft);

  return (
    <>
      <div className={styles.head}>
        <div className={styles.titleRow}>
          <h2 className={styles.title}>
            {formatRange(draft.beginDate, draft.endDate, locale) || (
              <Trans>New absence</Trans>
            )}
          </h2>

          <Button
            variant="ghost"
            size="sm"
            label={t`Overview`}
            leftSection={<Icon name="ChartPie" size="sm" />}
            onClick={() => deselect()}
          />
        </div>

        <div className={styles.fields}>
          <p className={styles.field}>
            <span className={styles.label}>
              <Trans>Reason</Trans>
            </span>

            <Select
              items={types.map((type) => ({
                label: type.label,
                value: String(type.id),
              }))}
              value={null === draft.typeId ? null : String(draft.typeId)}
              onValueChange={(value) =>
                void patch({ typeId: null === value ? null : Number(value) })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder={t`Choose a reason`} />
              </SelectTrigger>

              <SelectContent>
                {types.map((type) => (
                  <SelectItem key={type.id} value={String(type.id)}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </p>

          <p className={clsx(styles.field, styles.period)}>
            <span className={styles.label}>
              <Trans>Period</Trans>
            </span>

            <DatePicker
              range
              locale={locale}
              value={[draft.beginDate, draft.endDate]}
              onValueChange={([beginDate, endDate]) =>
                void patch({ beginDate, endDate })
              }
            >
              <DatePickerTrigger>
                <DatePickerValue placeholder={t`Start and end date`} />
              </DatePickerTrigger>
              <DatePickerContent />
            </DatePicker>
          </p>

          <p className={styles.field}>
            <span className={styles.label}>
              <Trans>Duration</Trans>
            </span>

            <span className={styles.readOnly}>
              <Trans>{dayCount} days</Trans>
            </span>
          </p>
        </div>

        <ValidatedHoursNotice />

        {/* Chaining absences is what makes them one case, and the case is
            what the day thresholds and the child sickness cap count on */}
        {selectedType?.caseLinkable && (
          <div className={styles.sameCase}>
            <p className={styles.field}>
              <span className={styles.label}>
                <Trans>Same case as</Trans>
              </span>

              <Select
                value={
                  null === draft.sameCaseAbsenceId
                    ? NEW_CASE
                    : String(draft.sameCaseAbsenceId)
                }
                onValueChange={(value) =>
                  void patch({
                    sameCaseAbsenceId:
                      null === value || NEW_CASE === value
                        ? null
                        : Number(value),
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue>
                    {(value: string) =>
                      NEW_CASE === value ? (
                        <Trans>New case</Trans>
                      ) : (
                        <AbsenceSameCaseLabel
                          absence={absences.find(
                            (absence) => String(absence.id) === value,
                          )}
                        />
                      )
                    }
                  </SelectValue>
                </SelectTrigger>

                <SelectContent>
                  <SelectItem value={NEW_CASE}>
                    <Trans>New case</Trans>
                  </SelectItem>

                  {sameCaseCandidates.map((absence) => (
                    <SelectItem key={absence.id} value={String(absence.id)}>
                      <AbsenceSameCaseLabel absence={absence} />
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </p>
          </div>
        )}

        <div className={styles.extra}>
          <p className={styles.field}>
            <span className={styles.label}>
              <Trans>Comment</Trans>
            </span>

            <Input
              value={draft.comment}
              onValueChange={(comment) => void patch({ comment })}
            />
          </p>

          <p className={styles.field}>
            <span className={styles.label}>
              <Trans>From time</Trans>
            </span>

            <TimePicker
              minuteStep={1}
              value={draft.beginTime}
              onValueChange={(beginTime) =>
                void patch({ beginTime: beginTime ?? "" })
              }
            >
              <TimePickerTrigger>
                <TimePickerValue />
              </TimePickerTrigger>
              <TimePickerContent />
            </TimePicker>
          </p>

          <p className={styles.field}>
            <span className={styles.label}>
              <Trans>To time</Trans>
            </span>

            <TimePicker
              minuteStep={1}
              value={draft.endTime}
              onValueChange={(endTime) =>
                void patch({ endTime: endTime ?? "" })
              }
            >
              <TimePickerTrigger>
                <TimePickerValue />
              </TimePickerTrigger>
              <TimePickerContent />
            </TimePicker>
          </p>
        </div>
      </div>

      <ReplacementList />
    </>
  );
}
